'use client';
import { VoiceCallMessage } from '../voice/VoiceCallTranscript';
import { failureHeadline, visibleToolDuration } from '../../lib/toolPresentationCore';
import { plainContent } from '../../lib/plainContent';
import { Fragment, memo, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Brain, Clock3, Users } from 'lucide-react';
import { ToolMark } from '../../components/ui/ToolMark';
import { Message as AIMessage, MessageContent } from '../../components/ui/shadcn/message';
import { Bubble, BubbleContent } from '../../components/ui/shadcn/bubble';
import { Trace, TraceCollapsible, TraceCollapsibleContent, TraceCollapsibleTrigger, TraceDescription, TraceIcon, TraceLine, TraceTitle } from '../../components/ui/shadcn/trace';
import { CodeBlock, CodeBlockContent } from '../../components/ui/shadcn/code-block';
import { TraceWaterfall, type TraceSpan } from '../../components/ui/shadcn/trace-waterfall';
import { interpolate, useTranslation } from '../../lib/i18n';
import { useNow } from '../../lib/useNow';
import type { BrainModelOption, Profile } from '../../lib/types';
import { reactionsForViewer } from '../../lib/reactions';
import { groupToolItems, submittedPlanOf, type ChatTurn, type ElowenTurn, type ToolItem } from '../../lib/transcript';
import { ModelIcon } from '../../components/ui/ModelIcon';
import { InteractiveView } from './InteractiveView';
import { PlanCard } from './PlanCard';
import { QuestionAnswerSummary } from './QuestionAnswerSummary';
import { formatDuration, localDateTime } from '../../lib/format';
import { Spinner } from '../../components/ui/states';
import { ShimmerText } from '../../components/ui/ShimmerText';
import { MotionFade, MotionPresence } from '../../components/ui/Motion';
import { brainModelLabel } from '../../lib/modelProvider';
import { DEFAULT_COMPOSE_MARKER_MS, DEFAULT_LONG_TOOL_COMPOSE_MARKER_MS, LONG_COMPOSE_TOOLS, composingLabel, type ComposeLocale } from '../../lib/chatPresentation';
import { ChatMarkdown } from './ChatMarkdown';
import { MentionText } from '../../components/ui/MentionChip';
import { AGENT_AVATAR_SRC } from './agentIdentity';
import { DiffBlock } from './ChatDiff';
import { ToolOutputBlock } from './ChatLog';
import { EventRows, SessionEvents } from './ChatEvents';
import { Attachments, ComposerFiles, SharedImage, SharedFile } from './ChatAttachments';
import { MessageBlocks } from './MessageBlocks';
import { steerDescription } from './agentPresentation';
import { splitNativeRuns, type NonAssistantTurn, type WorkNode } from './workRuns';
import { Avatar } from '../../components/ui/Avatar';
import { ReplyQuote, type ReplyQuoteState } from './ReplyQuote';
import { MessageReactions, useMessageReactions, writtenByViewer, type MessageReaction, type ReactionControl } from './MessageReactions';
import { MessageActions } from './MessageActions';
import { MessageEdit } from './MessageEdit';
import { isUnseenPosition } from './brainChatHistory';

/** How many trailing lines of a running command's live output tail to show (mirror of the CLI). */
const PROGRESS_TAIL_ROWS = 8;
/** The assistant's tool calls, rendered as tight monospace log rows stacked directly under each other —
 *  no pills, no chrome. A tool that produced a diff, a command output or a live progress tail is a
 *  collapsed-by-default row (chevron, expands on click); a tool with nothing to show is a plain row.
 *  Plain rows and collapsed summaries share the exact same padding, so a mixed run reads as one even
 *  column. The argument summary (file path, query…) rides muted next to the name. */
function ToolDuration({ tool }: { tool: ToolItem }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const ticking = tool.pending === true && tool.startedAt !== undefined;
  useEffect(() => {
    if (!ticking) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    // Observe the row even while its short duration label is hidden.
    observer.observe(ref.current!.parentElement!);
    return () => observer.disconnect();
  }, [ticking]);
  const now = useNow(1000, ticking && visible);
  const duration = visibleToolDuration(tool.pending && tool.startedAt !== undefined
    ? Math.max(0, now - tool.startedAt) : tool.durationMs);
  return (
    <span ref={ref} hidden={duration === undefined} data-testid="chat-tool-duration" className="shrink-0 tabular-nums opacity-50">
      {duration === undefined ? null : formatDuration(duration)}
    </span>
  );
}

function ToolPills({ tools }: { tools: ToolItem[] }) {
  const { t } = useTranslation();
  // Consecutive calls of the same tool fold into ONE row carrying a `×N` count (the CLI's grouped pills);
  // recomputed every render so a streaming run's count and latest argument stay live.
  const groups = groupToolItems(tools);
  return (
    <Trace>
      {groups.map((group, i) => {
        const tool = group.item;
        // A submitted plan REPLACES its tool row: the row would say only "ExitPlanMode", which tells the
        // reader nothing the panel does not say better.
        const plan = submittedPlanOf(tool);
        if (plan) return <div key={i} className="chat-log-plan">{tool.questionAnswer ? <QuestionAnswerSummary record={tool.questionAnswer} /> : null}<PlanCard plan={plan} id={tool.id} /></div>;
        if (tool.questionAnswer && tool.name === 'AskUserQuestion') return <div key={i} className="chat-log-plan"><QuestionAnswerSummary record={tool.questionAnswer} /></div>;
        const rich = !!(tool.diff || tool.output || tool.progress || tool.sub);
        const failed = tool.output?.tone === 'danger' || tool.sub?.status === 'error';
        const cause = failed && tool.output ? failureHeadline(tool.output, plainContent) : null;
        const runningTool = group.running;
        const running = runningTool !== undefined || (tool.sub?.status === 'running' && !tool.sub.steered);
        const detail = runningTool?.reason ?? tool.detail;
        const head = (
          <TraceLine status={running ? 'running' : failed ? 'error' : undefined}>
            <TraceIcon>
              {running
                ? <Spinner size="xs" label={t.brainChat.toolRunning} />
                : tool.sub ? <Users /> : <ToolMark name={tool.name} />}
            </TraceIcon>
            <TraceTitle className={tool.sub ? 'max-w-1/2 shrink-0' : 'whitespace-normal'}>
              {tool.sub?.name ?? tool.name}
              {!tool.sub && detail ? <span className="text-muted-foreground"> {detail}</span> : null}
              {cause ? <span className="text-destructive"> · {cause}</span> : null}
            </TraceTitle>
            {tool.sub?.steered ? <span data-testid="chat-steer-state" className={tool.sub.steerState === 'dropped' ? 'truncate text-destructive' : 'truncate opacity-70'}>{steerDescription(tool.sub, t.agents)}</span> : null}
            {group.count > 1 ? <TraceDescription>×{group.count}</TraceDescription> : null}
            {tool.sub
              ? <>{tool.sub.model ? <TraceDescription className="min-w-0 shrink truncate" title={tool.sub.model}>{brainModelLabel(tool.sub.model)}</TraceDescription> : null}<TraceDescription>{formatDuration(tool.sub.seconds * 1000)}</TraceDescription></>
              : <ToolDuration tool={runningTool ?? tool} />}
          </TraceLine>
        );
        if (!rich) {
          return <div key={i}>{tool.questionAnswer ? <QuestionAnswerSummary record={tool.questionAnswer} /> : null}<div data-testid="chat-tool-pill" data-tool-id={tool.id}>{head}</div></div>;
        }
        return (
          <div key={i}>
          {tool.questionAnswer ? <QuestionAnswerSummary record={tool.questionAnswer} /> : null}
          <TraceCollapsible data-testid="chat-tool-pill" data-tool-id={tool.id}>
            <TraceCollapsibleTrigger>{head}</TraceCollapsibleTrigger>
            <TraceCollapsibleContent className="flex flex-col gap-3">
                {tool.sub ? <Trace>
                  {tool.sub.task ? <p className="text-ui">{tool.sub.task}</p> : null}
                  {tool.sub.detail ? <TraceLine status={tool.sub.status === 'running' && !tool.sub.steered ? 'running' : tool.sub.status === 'error' ? 'error' : undefined}>
                    <TraceIcon><Clock3 /></TraceIcon>
                    <TraceTitle className="whitespace-normal">{tool.sub.detail}</TraceTitle>
                  </TraceLine> : null}
                </Trace> : null}
                {tool.diff ? <DiffBlock diff={tool.diff} path={tool.detail} /> : null}
                {/* A folded run of identical FAILURES keeps every member: the rows read alike, but each one
                    names the path it refused, so the expanded block lists them all. */}
                {group.members
                  ? group.members.map((member, j) => (member.output ? <ToolOutputBlock key={j} output={member.output} /> : null))
                  : tool.output ? <ToolOutputBlock output={tool.output} /> : null}
                {tool.progress ? <ProgressBlock text={tool.progress} /> : null}
            </TraceCollapsibleContent>
          </TraceCollapsible>
          </div>
        );
      })}
    </Trace>
  );
}

/** One reasoning segment, the web twin of the CLI's "Thought: 12s" row: a collapsible block whose header
 *  carries how long the model has been thinking. It stays open while it streams and folds itself away once
 *  the turn settles — unless the reader has toggled it, in which case their choice wins for good. The
 *  elapsed time is measured here (the wire carries no timing), so it exists only for a segment this tab
 *  actually watched stream; a rehydrated one simply shows the time it spent mounted. */
function ReasoningBlock({ text, live }: { text: string; live: boolean }) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<'auto' | 'open' | 'closed'>('auto');
  const startRef = useRef(Date.now());
  const now = useNow(1000, live);
  const [settledElapsed, setSettledElapsed] = useState<number | null>(null);
  useEffect(() => { if (!live) setSettledElapsed(Date.now() - startRef.current); }, [live]);
  const elapsed = settledElapsed ?? Math.max(0, now - startRef.current);
  const open = choice === 'auto' ? live : choice === 'open';
  // A button + conditional body rather than <details>: the open state is driven by the stream (it folds
  // itself when the turn settles), and a details element owns its own toggling, which fights that.
  return (
    <Trace data-testid="chat-thought" className="my-1.5">
      <TraceCollapsible open={open} onOpenChange={next => setChoice(next ? 'open' : 'closed')}>
        <TraceCollapsibleTrigger>
          <TraceLine status={live ? 'running' : undefined}>
            <TraceIcon><Brain /></TraceIcon>
            <TraceTitle>{t.brainChat.reasoningLabel}</TraceTitle>
            <TraceDescription>{formatDuration(elapsed)}</TraceDescription>
          </TraceLine>
        </TraceCollapsibleTrigger>
        <TraceCollapsibleContent className="whitespace-pre-wrap text-meta">{text}</TraceCollapsibleContent>
      </TraceCollapsible>
    </Trace>
  );
}

/** Live rolling tail of a running Bash (the `tool_progress` event): the last lines of its output
 *  as it streams, in a muted terminal block. Cleared once the final `output`/`diff` lands, so it never
 *  doubles the final dump. */
function ProgressBlock({ text }: { text: string }) {
  return (
    <CodeBlock size="sm">
      <CodeBlockContent wrap className="max-h-64">{text.split('\n').slice(-PROGRESS_TAIL_ROWS).join('\n')}</CodeBlockContent>
    </CodeBlock>
  );
}

/** A context-compaction boundary: a subtle labelled divider standing in for the summarized-away history. */
function ContextDivider() {
  const { t } = useTranslation();
  return (
    <div data-testid="chat-turn" data-role="divider" className="flex items-center gap-2 text-small text-muted-foreground my-5" role="separator">
      <span className="h-px flex-1 bg-border" aria-hidden />
      <span className="shrink-0 uppercase text-eyebrow">{t.brainChat.contextCompacted}</span>
      <span className="h-px flex-1 bg-border" aria-hidden />
    </div>
  );
}

/** Settled-turn metadata: when the turn ended and how long it took.
 *
 *  One visible reply is MANY assistant messages — the agent writes one per tool round — and every one of
 *  them carries its own `createdAt`. Stamping each turn therefore printed the same date+time above every
 *  single tool row. The daemon marks the run's LAST assistant row alone with `turn_duration_ms`
 *  (store/schema.sql), and that is exactly the marker the CLI gates its own settled meta on
 *  (src/cli/chat/turnRenderer.ts) — so keying off it puts one stamp per turn on both surfaces instead of
 *  inventing a second, web-only notion of where a turn ends. A still-streaming turn has not ended yet and
 *  carries no duration, so it stays unstamped until it settles.
 *
 *  A person's message carries this line below its bubble; an answer carries it in the head on desktop and
 *  below the finished reply on a phone. `head` removes the margin already supplied by the head line. */
function MessageMeta({ turn, models, showDuration = true, actions, head = false, compactTime = false }: {
  turn: Extract<ChatTurn, { role: 'you' | 'elowen' }>;
  models?: readonly BrainModelOption[];
  /** False when the run's work summary already says how long the run took. */
  showDuration?: boolean;
  /** The message's own controls and reactions, inline at the end of this line: the one row both roles use. */
  actions?: ReactNode;
  /** The answer placement: the run's head line, above the reply, instead of a line of the message's own. */
  head?: boolean;
  compactTime?: boolean;
}) {
  const { locale, t } = useTranslation();
  const settled = turn.role === 'elowen' ? turn.durationMs != null : Boolean(turn.createdAt);
  if (!settled && !actions) return null;
  // A folded run already shows its time in the summary; without a stamp or a model there is nothing left.
  if (!actions && turn.role === 'elowen' && !showDuration && !turn.createdAt && !turn.model) return null;
  const modelLabel = turn.role === 'elowen' && turn.model ? brainModelLabel(turn.model, models) : '';
  return (
    <div data-testid="chat-turn-meta" data-role={turn.role === 'you' ? 'user' : 'assistant'} data-placement={head ? 'head' : 'message'} className={`chat-turn-meta ${head ? 'min-w-0 flex-1' : 'mt-1.5'} flex flex-wrap items-center ${compactTime ? 'gap-x-1' : 'gap-x-2'} text-small text-muted-foreground`}>
      {turn.createdAt ? <time dateTime={turn.createdAt} title={compactTime ? localDateTime(turn.createdAt, locale, false) : undefined} className="whitespace-nowrap">{compactTime && Number.isFinite(Date.parse(turn.createdAt)) ? new Date(turn.createdAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : localDateTime(turn.createdAt, locale, false)}</time> : null}
      {turn.role === 'elowen' && turn.model ? (
        <span data-testid="chat-turn-model" className="inline-flex min-w-0 items-center gap-1" title={turn.model}>
          <ModelIcon name={modelLabel} size={10} />
          <span className="max-w-48 truncate">{modelLabel}</span>
        </span>
      ) : null}
      {turn.role === 'elowen' && showDuration && turn.durationMs != null ? (
        <span className="inline-flex items-center gap-1" title={t.brainChat.turnDuration}>
          <Clock3 size={10} aria-hidden />
          {formatDuration(turn.durationMs)}
        </span>
      ) : null}
      {actions}
    </div>
  );
}

function ToolAuthoringHint({ turn, locale }: {
  turn: Extract<ChatTurn, { role: 'elowen' }>;
  locale: ComposeLocale;
}) {
  const { t } = useTranslation();
  const startedAt = useRef(Date.now());
  const [ready, setReady] = useState(false);
  const threshold = turn.composingTool && LONG_COMPOSE_TOOLS.has(turn.composingTool)
    ? DEFAULT_LONG_TOOL_COMPOSE_MARKER_MS
    : DEFAULT_COMPOSE_MARKER_MS;

  useEffect(() => {
    const remaining = Math.max(0, threshold - (Date.now() - startedAt.current));
    if (remaining === 0) { setReady(true); return; }
    setReady(false);
    const timer = setTimeout(() => setReady(true), remaining);
    return () => clearTimeout(timer);
  }, [threshold]);

  if (!turn.streaming || !turn.composing || !ready) return null;
  const label = composingLabel(turn.composingReason, turn.composingTool, turn.composingDetail, locale)
    ?? turn.composingTool
    ?? t.brainChat.toolRunning;
  return (
    <TraceLine data-testid="chat-tool-authoring" role="status" aria-live="polite" status="running">
      <TraceIcon><Spinner size="xs" /></TraceIcon>
      <TraceTitle>{label}</TraceTitle>
    </TraceLine>
  );
}

type Segment = ElowenTurn['segments'][number];

/** One segment of an assistant step. The work timeline and the visible tail draw it the same way.
 *  Memoized on the segment's identity: a token landing in a run's live step re-renders the run, and its
 *  settled steps must not be drawn again with it. */
const SegmentView = memo(function SegmentView({ seg, anchor, showThoughts, live, messageId, mentionsYou, mentionUnseen, mentions, people, viewerId }: {
  seg: Segment;
  anchor: string;
  showThoughts: boolean;
  live: boolean;
  /** The journal jump target: the last text, or the first visible segment of a textless step. */
  messageId?: string;
  /** Set on the first text of a step whose Address mentions the viewer: highlighted, and said so to assistive tech. */
  mentionsYou?: boolean;
  mentionUnseen?: boolean;
  /** The step's Address mentions, drawn as chips where its text writes them. */
  mentions?: ElowenTurn['mentions'];
  people?: ReadonlyMap<number, Profile>;
  viewerId?: number;
}) {
  const { t } = useTranslation();
  const text = seg.kind === 'text' ? <ChatMarkdown text={seg.text} className="my-1.5" mentions={mentions} people={people} viewerId={viewerId} /> : null;
  const node = seg.kind === 'text'
    ? (mentionsYou
      ? (
        <div data-mentions-you="" data-mention-unseen={mentionUnseen ? '' : undefined} className="chat-mention">
          {text}
          <span className="sr-only">{t.chat.mentionsYou}</span>
        </div>
      )
      : text)
    : seg.kind === 'reasoning'
    ? (showThoughts ? <ReasoningBlock text={seg.text} live={live} /> : null)
    : seg.kind === 'image'
    ? <SharedImage image={seg.image} caption={seg.caption} />
    : seg.kind === 'view'
    ? <InteractiveView id={seg.id} view={seg.view} waiting={seg.waiting} />
    : seg.kind === 'file'
    ? <SharedFile file={seg.file} caption={seg.caption} />
    : seg.kind === 'blocks'
    ? <MessageBlocks blocks={seg.blocks} />
    : <ToolPills tools={seg.items} />;
  if (node === null) return null;
  return <div className="chat-turn__segment" data-segment={seg.kind} data-chat-anchor={anchor} data-message-id={messageId}>{node}</div>;
});

/** The reaction row of a settled message the viewer may react to, and the optimistic state behind it.
 *  `reactions` are the message's own; which of them are the viewer's follows from their reactors, because
 *  a live fold cannot know who is reading. */
function Reactable({ messageId, reactions, control, className }: {
  messageId: string;
  reactions: readonly MessageReaction[];
  control: ReactionControl;
  className: string;
}) {
  const viewed = useMemo(() => reactionsForViewer(reactions, control.viewer.id), [reactions, control.viewer.id]);
  const state = useMessageReactions(messageId, viewed, control);
  return <MessageReactions reactions={state.reactions} onToggle={state.toggle} className={className} />;
}

/** A message's stored reactions for a viewer who may not react to it (their own message, administrator
 *  oversight, a child view): the same row, counts and reactors, and the viewer's own pressed state, read-only. */
function StoredReactions({ reactions, viewerId, className }: { reactions: readonly MessageReaction[]; viewerId?: number; className: string }) {
  const viewed = useMemo(() => (viewerId === undefined ? reactions : reactionsForViewer(reactions, viewerId)), [reactions, viewerId]);
  return <MessageReactions reactions={viewed} className={className} />;
}

const NO_REACTIONS: readonly MessageReaction[] = [];

/** THE action row of a message, for a person and for the agent alike: it lives inline in the message's own
 *  metadata line, right after the timestamp, so it costs the message no line of its own. `chat.css` sizes its
 *  glyph to that line's em and keeps a 24px pointer target behind it without growing the line. */
const ACTION_ROW = 'chat-message-actions inline-flex items-center';

/** The message's own actions (copy, reply, edit/regenerate), or nothing while it has no stored id. */
function messageActions(message: { id?: string; role: 'you' | 'elowen'; settled: boolean }, text: string, readOnly?: boolean): ReactNode {
  return message.id
    ? <MessageActions text={text} id={message.id} role={message.role} settled={message.settled} readOnly={readOnly} />
    : null;
}

/** The reaction row a stored message carries, or null: interactive where the viewer may react to it, else
 *  read-only and only once someone reacted. It shares the metadata line, so it takes no margins of its own. */
function reactionRow(message: { id?: string; role: 'you' | 'elowen'; settled: boolean; author?: Profile; reactions?: readonly MessageReaction[] }, reacting: ReactionControl | undefined, viewerId: number | undefined, readOnly?: boolean): ReactNode {
  const reactions = message.reactions ?? NO_REACTIONS;
  return message.settled && !readOnly && reacting && message.id && !writtenByViewer(message, reacting)
    ? <Reactable messageId={message.id} reactions={reactions} control={reacting} className="" />
    : reactions.length ? <StoredReactions reactions={reactions} viewerId={viewerId} className="" /> : null;
}

/** One message's row: its own controls and its reactions together, for the metadata line that message owns. */
function messageActionRow(message: { id?: string; role: 'you' | 'elowen'; settled: boolean; author?: Profile; reactions?: readonly MessageReaction[] }, reacting: ReactionControl | undefined, viewerId: number | undefined, text: string, readOnly?: boolean): ReactNode {
  const actions = messageActions(message, text, readOnly);
  const reactions = reactionRow(message, reacting, viewerId, readOnly);
  return actions || reactions ? <div className={ACTION_ROW}>{actions}{reactions}</div> : null;
}

/** The quote of the message one assistant step replies to, with its jump. */
export type ReplyQuoteView = { quote: ReplyQuoteState; onJump: () => void };
/** Answers which quote, if any, an assistant step carries. The caller resolves the target, so the
 *  function's identity changes when a resolution does and the memoized run draws it. */
export type ReplyQuoteFor = (step: ElowenTurn | Extract<ChatTurn, { role: 'you' }>) => ReplyQuoteView | null;

/** One disclosure for the group's work. Its clock starts at the opener's UTC timestamp or the
 * local send time, including the wait between native turns, and ends at the last terminal timestamp. */
const WorkSummary = memo(function WorkSummary({ open, controls, live, startedAt, durationMs, outcome }: {
  open: boolean;
  controls: string;
  live: boolean;
  startedAt?: string;
  durationMs?: number;
  outcome?: 'stopped' | 'failed';
}) {
  const { t } = useTranslation();
  const mountedAt = useRef(Date.now());
  const now = useNow(1000, live);
  const start = startedAt ? Date.parse(startedAt) : mountedAt.current;
  const duration = durationMs === undefined ? undefined : { duration: formatDuration(durationMs) };
  const label = outcome === 'stopped'
    ? (duration ? interpolate(t.brainChat.workStoppedAfter, duration) : t.brainChat.workStopped)
    : outcome === 'failed'
    ? (duration ? interpolate(t.brainChat.workFailedAfter, duration) : t.brainChat.workFailed)
    : duration ? interpolate(t.brainChat.workDuration, duration) : t.brainChat.workLog;
  // The live and settled labels swap in a cross-fade: the presence keeps the leaving label on screen for the fade.
  return (
    <TraceCollapsibleTrigger
      data-testid="chat-work-summary"
      data-live={live ? '' : undefined}
      aria-expanded={open}
      aria-controls={controls}
      className="chat-work__summary py-0.5"
    >
      <TraceLine status={outcome === 'failed' ? 'error' : undefined}>
      <TraceIcon><Clock3 /></TraceIcon>
      <TraceTitle>
      <MotionPresence>
        <MotionFade key={live ? 'live' : 'settled'} className="flex items-center gap-1.5">
          {live
            ? <><ShimmerText>{t.brainChat.workLive}</ShimmerText><span className="tabular-nums opacity-60">{formatDuration(Math.max(0, now - start))}</span></>
            : <span>{label}</span>}
        </MotionFade>
      </MotionPresence>
      </TraceTitle>
      </TraceLine>
    </TraceCollapsibleTrigger>
  );
});

/** The work timeline: every step of the run as one indented item, in order. */
function WorkTimeline({ id, nodes, hint, segmentKey, drawSegment }: {
  id: string;
  nodes: WorkNode[];
  hint: ReactNode;
  segmentKey: (segment: Segment) => string;
  /** Draws one segment as the visible tail draws it, with its message's quote and reactions. */
  drawSegment: (segment: Segment) => ReactNode;
}) {
  return (
    <Trace id={id} data-testid="chat-work-timeline">
      <ol className="flex min-w-0 flex-col gap-2">
        {nodes.map((node) => (
          <li key={node.kind === 'segment' ? segmentKey(node.segment) : node.events[0]!.event.id} className="min-w-0" data-testid="chat-work-step" data-step={node.kind === 'segment' ? node.segment.kind : 'events'}>
            {node.kind === 'events'
              ? <EventRows events={node.events} />
              : drawSegment(node.segment)}
          </li>
        ))}
        {hint ? <li className="min-w-0">{hint}</li> : null}
      </ol>
    </Trace>
  );
}

/** Timing is optional on older history. Never reconstruct missing tool intervals from row order. */
function RunWaterfall({ turns, startedAt, durationMs, live, outcome }: { turns: readonly ChatTurn[]; startedAt?: string; durationMs?: number; live: boolean; outcome?: 'stopped' | 'failed' }) {
  const { t } = useTranslation();
  const now = useNow(1000, live);
  const start = startedAt ? Date.parse(startedAt) : undefined;
  const total = durationMs ?? (live && start !== undefined && Number.isFinite(start) ? Math.max(0, now - start) : undefined);
  if (total === undefined) return null;
  const spans: TraceSpan[] = [{ id: 'run', name: t.brainChat.workLog, depth: 0, startMs: 0, durationMs: total, status: live ? 'running' : outcome === 'failed' ? 'failed' : 'completed' }];
  if (start !== undefined && Number.isFinite(start)) {
    for (const turn of turns) {
      if (turn.role !== 'elowen') continue;
      for (const segment of turn.segments) {
        if (segment.kind !== 'tools') continue;
        for (const tool of segment.items) {
          if (tool.startedAt === undefined || !Number.isFinite(tool.startedAt)) continue;
          const duration = tool.durationMs ?? (tool.pending ? Math.max(0, now - tool.startedAt) : undefined);
          if (duration === undefined || !Number.isFinite(duration)) continue;
          spans.push({ id: tool.id ?? `tool-${spans.length}`, name: tool.name, depth: 1, startMs: Math.max(0, tool.startedAt - start), durationMs: duration,
            status: tool.pending ? 'running' : tool.output?.tone === 'danger' ? 'failed' : 'completed' });
        }
      }
    }
  }
  return <TraceWaterfall data-testid="chat-run-waterfall" className="mt-3" spans={spans} totalMs={total} visibleCount={spans.length} />;
}

type RunProps = {
  /** The run's turns: its assistant steps and the event rows between them (see `transcriptBlocks`). */
  turns: readonly ChatTurn[];
  models?: readonly BrainModelOption[];
  showRole?: boolean;
  breakBefore?: boolean;
  /** The transcript's first turn: it sits on the scroller's own padding whatever sibling precedes it. */
  first?: boolean;
  showThoughts: boolean;
  /** The block's reading-anchor identity (`data-tk`): its last stored step, which a prepended page cannot change. */
  tk?: string;
  /** This run is the conversation's current work. */
  live: boolean;
  outcome?: 'stopped' | 'failed';
  /** When the person's message that started this run was written. */
  startedAt?: string;
  /** The submitted plan still awaiting its decision, which a collapsed run must keep visible. */
  pendingPlanId?: string;
  /** Which reply quote each step carries; absent where no step can carry one. */
  quoteFor?: ReplyQuoteFor;
  /** Present when the viewer may react; every stored step with text is then reactable. */
  reacting?: ReactionControl;
  readOnly?: boolean;
  /** Undefined until read state loads; null means no message has been read. */
  mentionReadDepth?: number | null;
  /** The signed-in account: a step whose Address mentions it is highlighted, and stored reactions it made are pressed. */
  viewerId?: number;
  /** The conversation's members by account id, for the mention chips in the steps' text. */
  people?: ReadonlyMap<number, Profile>;
  /** A jump to one of this run's messages: each new request opens the work timeline, as the reader's own
   *  toggle would, so the target is drawn rather than focused inside folded content. */
  reveal?: { nonce: number };
  /** A phone-width transcript: the author column takes the smaller avatar. */
  phone?: boolean;
};

/** A run's turn array is rebuilt on every fold, but its settled turns keep their identity, so the memo
 *  compares the turns one by one. */
function sameRun(a: RunProps, b: RunProps): boolean {
  return a.turns.length === b.turns.length && a.turns.every((turn, i) => turn === b.turns[i])
    && a.models === b.models && a.showRole === b.showRole && a.breakBefore === b.breakBefore && a.first === b.first
    && a.showThoughts === b.showThoughts && a.tk === b.tk && a.live === b.live && a.outcome === b.outcome
    && a.startedAt === b.startedAt && a.pendingPlanId === b.pendingPlanId
    && a.quoteFor === b.quoteFor && a.reacting === b.reacting && a.viewerId === b.viewerId && a.reveal === b.reveal
    && a.phone === b.phone && a.people === b.people && a.readOnly === b.readOnly
    && a.mentionReadDepth === b.mentionReadDepth;
}

/** One agent run, the way the ChatGPT app shows it: while the agent works, its reasoning, tool rows and
 *  interim messages grow as a timeline under "Working…"; once the run settles, the timeline folds into
 *  one "Worked for 24s" line above the visible tail (the closing text, plan or question). The reader's
 *  own toggle wins over the automatic open/closed state for good, as on the reasoning block. A run with
 *  nothing but its tail draws no disclosure after it settles; a live run always shows its header.
 *
 *  Memoized for the same reason as `Message`: a streamed token rebuilds the transcript, and only the
 *  live run's props change with it. */
export const AssistantRun = memo(function AssistantRun({ turns, models, showRole, breakBefore, first, showThoughts, tk, live, outcome, startedAt, pendingPlanId, quoteFor, reacting, viewerId, people, reveal, phone, readOnly, mentionReadDepth }: RunProps) {
  const { t, locale } = useTranslation();
  const timelineId = useId();
  const [choice, setChoice] = useState<'auto' | 'open' | 'closed'>('auto');
  const [revealed, setRevealed] = useState<number | null>(null);
  if (reveal && reveal.nonce !== revealed) {
    setRevealed(reveal.nonce);
    setChoice('open');
  }
  const steps = turns.filter((turn): turn is ElowenTurn => turn.role === 'elowen');
  const last = steps[steps.length - 1];
  const parts = splitNativeRuns(turns, pendingPlanId, live, outcome).map(part => ({
    ...part,
    work: showThoughts ? part.work : part.work.filter(node => node.kind !== 'segment' || node.segment.kind !== 'reasoning'),
  }));
  // A page can extend THIS run. Segment identities follow their source step instead of their position
  // in the run, keeping both the DOM and the reading anchor stable through a prepend.
  const segmentKeys = new Map<Segment | ToolItem, string>();
  const firstLive = turns.findIndex(turn => !turn.id);
  turns.forEach((turn, i) => {
    if (turn.role !== 'elowen') return;
    turn.segments.forEach((segment, j) => {
      const key = JSON.stringify([turn.id ?? `live-${i - firstLive}`, j]);
      segmentKeys.set(segment, key);
      if (segment.kind === 'tools') segment.items.forEach((item, k) => segmentKeys.set(item, `${key}:${k}`));
    });
  });
  // Each step is one assistant message. Its reply quote sits above its first text; a live step whose text
  // has not streamed yet keeps its quote at the run's end, which is where that text will appear. A stored
  // step's last text carries the message's id (the jump target) and, when the viewer may react, its own
  // reactions: an earlier message of the run keeps them under the fold rather than lending them to the last.
  // An Address that mentions the viewer marks the same first text its reply quote sits above, and every text
  // of the step draws the Address mentions it writes as chips.
  const quotes = new Map<Segment, ReplyQuoteView>();
  const waitingQuotes: ReplyQuoteView[] = [];
  const messageEnds = new Map<Segment, { id: string; step: ElowenTurn }>();
  const mentioning = new Set<Segment>();
  const unseenMentions = new Set<Segment>();
  const stepMentions = new Map<Segment, ElowenTurn['mentions']>();
  for (const step of steps) {
    if (step.mentions?.length) for (const segment of step.segments) if (segment.kind === 'text') stepMentions.set(segment, step.mentions);
    const view = quoteFor?.(step);
    const text = step.segments.find((segment) => segment.kind === 'text');
    if (view && text) quotes.set(text, view);
    else if (view) waitingQuotes.push(view);
    if (text && viewerId !== undefined && step.mentions?.some((mention) => mention.id === viewerId)) {
      mentioning.add(text);
      if (isUnseenPosition(step.branchDepth, mentionReadDepth)) unseenMentions.add(text);
    }
    const end = step.segments.findLast((segment) => segment.kind === 'text');
    if (step.id && !step.streaming && end) messageEnds.set(end, { id: step.id, step });
  }
  const segmentKey = (segment: Segment): string => {
    const key = segmentKeys.get(segment.kind === 'tools' ? segment.items[0]! : segment);
    if (key === undefined) throw new Error('Transcript segment has no source step');
    return key;
  };
  const displayed = parts.flatMap(part => [...part.work.flatMap(node => node.kind === 'segment' ? [node.segment] : []), ...part.waiting, ...part.tail]);
  // Read cursors also name assistant messages containing only tools. Give their first displayed
  // segment an anchor too; text-message anchors stay on the last text beside their reactions.
  const nonTextAnchors = new Map<Segment, string>();
  for (const step of steps) {
    if (!step.id || step.streaming || step.segments.some(segment => segment.kind === 'text')) continue;
    const keys = step.segments.map(segment => segmentKeys.get(segment)).filter((key): key is string => key !== undefined);
    const anchor = displayed.find(segment => keys.some(key => segmentKey(segment) === key || segmentKey(segment).startsWith(`${key}:`)));
    if (anchor) nonTextAnchors.set(anchor, step.id);
  }
  // With a human opener the run spans opener → last completion. Without one (scheduled or continuation runs)
  // the first step's timestamp is a completion, not a start, so its span undercounts (0 s for one step);
  // the stored turn duration then stays the floor.
  const start = startedAt ?? steps[0]?.createdAt;
  const span = (from: string | undefined) => from && last?.createdAt ? Math.max(0, Date.parse(last.createdAt) - Date.parse(from)) : undefined;
  const durationMs = last?.durationMs == null ? undefined
    : startedAt ? span(startedAt) ?? last.durationMs
    : Math.max(last.durationMs, span(start) ?? 0);
  const liveSegment = last?.streaming ? last.segments[last.segments.length - 1] : undefined;
  const lastStep = steps[steps.length - 1];
  const stepText = (step: ElowenTurn): string => step.segments.flatMap(part => part.kind === 'text' ? [part.text] : []).join('\n');
  const stepActionRow = (step: ElowenTurn): ReactNode => messageActionRow(step, reacting, viewerId, stepText(step), readOnly);
  const drawSegment = (segment: Segment): ReactNode => {
    const quote = quotes.get(segment);
    const end = messageEnds.get(segment);
    const view = <SegmentView seg={segment} anchor={segmentKey(segment)} showThoughts={showThoughts} live={segment === liveSegment} messageId={end?.id ?? nonTextAnchors.get(segment)} mentionsYou={mentioning.has(segment)} mentionUnseen={unseenMentions.has(segment)}
      mentions={stepMentions.get(segment)} people={people} viewerId={viewerId} />;
    // The daemon keeps an open run's assistant entries provisional until the run settles and answers a reaction
    // to one with 404 `unknown_message`, so the row waits for the run to settle (the id still anchors jumps).
    // The LAST message of the run states its row in the run's own metadata line; an earlier message of the run
    // keeps its row under its own text, inside the work fold that holds it.
    const row = end && !live && end.step !== lastStep ? stepActionRow(end.step) : null;
    // A settled message's block holds its text, and its row only while that row is not the run's closing one:
    // the run's metadata line states that one. The block itself stays, so the dock's tail measurement and a
    // jump anchor still find one block per message.
    const message = end && !live
      ? <div data-testid="chat-message-reactable" className="chat-turn__reactable group/reactable min-w-0">{view}{row ? <div className="flex justify-end py-1">{row}</div> : null}</div>
      : view;
    return <>{quote ? <ReplyQuote quote={quote.quote} onJump={quote.onJump} /> : null}{message}</>;
  };
  const hint = last?.composing ? <ToolAuthoringHint turn={last} locale={locale as ComposeLocale} /> : null;
  const hasWork = live || parts.length > 1 || parts.some(part => part.work.length > 0);
  const closingActions = lastStep && !live ? stepActionRow(lastStep) : null;
  const headMeta = !phone && last ? <MessageMeta turn={last} models={models} showDuration={!hasWork} actions={closingActions} head /> : null;
  const footerMeta = phone && !live && last && !last.streaming
    ? <MessageMeta turn={last} models={models} actions={closingActions} compactTime /> : null;
  const open = choice === 'auto' ? live : choice === 'open';
  const toggleWork = useCallback((next: boolean) => setChoice(next ? 'open' : 'closed'), []);

  const body = (
    <Trace>
    <TraceCollapsible open={open} onOpenChange={toggleWork}>
      {hasWork ? (
        <WorkSummary
          open={open}
          controls={parts.length ? parts.map((_, i) => `${timelineId}-panel-${i}`).join(' ') : `${timelineId}-panel`}
          live={live}
          startedAt={start}
          durationMs={durationMs}
          outcome={outcome}
        />
      ) : null}
      {parts.map((part, native) => (
        <Fragment key={native}>
          {hasWork ? (
            <TraceCollapsibleContent id={`${timelineId}-panel-${native}`}>
              <WorkTimeline segmentKey={segmentKey} id={native === 0 ? timelineId : `${timelineId}-${native}`} nodes={part.work} hint={native === parts.length - 1 ? hint : null} drawSegment={drawSegment} />
              {native === parts.length - 1 ? <RunWaterfall turns={turns} startedAt={start} durationMs={durationMs} live={live} outcome={outcome} /> : null}
            </TraceCollapsibleContent>
          ) : null}
          {[...part.waiting, ...part.tail].map((seg) => (
            <Fragment key={segmentKey(seg)}>
              {drawSegment(seg)}
            </Fragment>
          ))}
        </Fragment>
      ))}
      {!parts.length && hasWork ? (
        <TraceCollapsibleContent id={`${timelineId}-panel`}>
          <WorkTimeline segmentKey={segmentKey} id={timelineId} nodes={[]} hint={hint} drawSegment={drawSegment} />
        </TraceCollapsibleContent>
      ) : null}
      {!hasWork ? hint : null}
      {waitingQuotes.map((view, i) => <ReplyQuote key={i} quote={view.quote} onJump={view.onJump} />)}
      {footerMeta}
    </TraceCollapsible>
    </Trace>
  );

  // Every run, in any kind of conversation (solo, shared, channel, job), takes the author column and head a
  // person's shared message uses, so Chetty always appears with its avatar; phone metadata follows the body.
  const name = t.chat.roleElowen;
  return (
    <AIMessage data-tk={tk} data-testid="chat-turn" data-role="assistant" data-first={first ? '' : undefined} data-continues={breakBefore ? undefined : ''} className="chat-turn chat-turn--assistant chat-turn--authored group/reactable">
      <div className={phone ? 'chat-author-run chat-author-run--phone min-w-0 w-full' : 'chat-author-run min-w-0 w-full'}>
        {showRole
          ? <AuthorHead name={name} avatar={<Avatar user={{ id: 0, username: name }} src={AGENT_AVATAR_SRC} size={!phone ? AUTHOR_AVATAR.regular : AUTHOR_AVATAR.small} />}>
              {headMeta ? <><span aria-hidden className="text-muted-foreground">·</span>{headMeta}</> : null}
            </AuthorHead>
          : <><span className="sr-only">{name}</span>{headMeta}</>}
        <MessageContent data-testid="chat-assistant-body" className="chat-turn__body">{body}</MessageContent>
      </div>
    </AIMessage>
  );
}, sameRun);

type MessageProps = {
  turn: NonAssistantTurn;
  models?: readonly BrainModelOption[];
  showRole?: boolean;
  breakBefore?: boolean;
  /** The transcript's first turn: it sits on the scroller's own padding whatever sibling precedes it. */
  first?: boolean;
  tk?: string;
  /** The signed-in account: a shared message that mentions it is highlighted and says so to assistive
   *  tech, and stored reactions it made are pressed. */
  viewerId?: number;
  /** Whether this message lies beyond the viewer's confirmed read boundary. */
  mentionUnseen?: boolean;
  /** Present when the viewer may react; the message then becomes reactable once it is stored. */
  reacting?: ReactionControl;
  readOnly?: boolean;
  /** The conversation's members by account id, for the chips of a shared message's mentions. */
  people?: ReadonlyMap<number, Profile>;
  /** A shared conversation's message whose previous turn is the same writer's message: it hangs in that
   *  run's column without repeating the avatar, name and time. */
  continuesRun?: boolean;
  /** A phone-width transcript: the author column takes the smaller avatar. */
  phone?: boolean;
  quoteFor?: ReplyQuoteFor;
};

/** The author column's avatar diameters in px, the `Avatar` size scale. They match the column widths in
 *  `.chat-author-run` (chat.css): the regular one on desktop and the small one on a phone. */
const AUTHOR_AVATAR = { regular: 28, small: 24 } as const;

/** The head of an author run, a person's or the agent's: the avatar in its zero-height slot left of the
 *  column (decorative, the name beside it is the text), then the name line with whatever follows it. */
function AuthorHead({ avatar, name, children }: { avatar: ReactNode; name: string; children?: ReactNode }) {
  return (
    <div className="chat-author-run__head">
      <span className="chat-author-run__avatar" aria-hidden>{avatar}</span>
      {/* The caption role is stated once here, so the name, the separator and the stamp share one size and the
          message's action row, which this line carries, is sized in the same em. */}
      <div data-testid="chat-run-author" className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 pb-0.5 text-small">
        <span className="min-w-0 truncate text-foreground">{name}</span>
        {children}
      </div>
    </div>
  );
}

/** The person's bubble: its text or call and attachments. The caller owns the message's
 *  `MessageEdit` scope, because the Edit control can sit outside the bubble, in the author's head line. */
function UserBubble({ turn, editor, people, viewerId, quote }: {
  turn: Extract<NonAssistantTurn, { role: 'you' }>;
  /** The open inline editor, which replaces the text while the message is being edited. */
  editor: ReactNode;
  quote?: ReplyQuoteView | null;
  /** A shared conversation's members by account id, for the chips of the message's mentions. */
  people?: ReadonlyMap<number, Profile>;
  viewerId?: number;
}) {
  const { t } = useTranslation();
  const text = turn.mentions?.length
    ? <MentionText text={turn.text} mentions={turn.mentions} people={people} viewerId={viewerId} prefix={t.chat.mentionChip} agentSrc={AGENT_AVATAR_SRC} />
    : turn.text;
  return (
    <Bubble align="end" data-testid="chat-user-bubble" data-editing={editor ? '' : undefined}
      className={editor ? 'w-full max-w-full' : undefined}>
      <BubbleContent className={editor ? 'w-full' : undefined}>
      {quote ? <ReplyQuote quote={quote.quote} onJump={quote.onJump} /> : null}
      {editor ?? (turn.voiceCall ? <VoiceCallMessage call={turn.voiceCall} text={turn.text} full />
        : (turn.text.trim() ? <div className="whitespace-pre-wrap text-conversation">{text}</div> : null))}
      {turn.images?.length ? <Attachments images={turn.images} attached /> : null}
      {turn.attachments?.length ? <ComposerFiles files={turn.attachments} /> : null}
      </BubbleContent>
    </Bubble>
  );
}

/** A shared conversation's human message: the writer's avatar in a column of its own to the left of the
 *  bubble, with the name and time above the first bubble of a run of their consecutive messages
 *  (`BrainChatSurface` decides where a run continues). A message that continues the run hangs in the same
 *  column, naming its writer only to assistive tech. The header carries the first message's time, so that
 *  first bubble has no metadata line; a continuing message keeps its own time below the bubble. A message that
 *  mentions the viewer says so to assistive tech; an unseen mention gets a quiet marker. */
function AuthoredMessage({ turn, author, breakBefore, first, tk, continuesRun, phone, meta, actions, mentionsYou, mentionUnseen, people, viewerId, quote, readOnly }: {
  turn: Extract<NonAssistantTurn, { role: 'you' }>;
  author: Profile;
  readOnly?: boolean;
  breakBefore?: boolean;
  first?: boolean;
  tk?: string;
  continuesRun?: boolean;
  phone?: boolean;
  meta: ReactNode;
  /** The message's own controls: this header line carries the message's time, so it carries the row too. */
  actions?: ReactNode;
  mentionsYou: boolean;
  mentionUnseen?: boolean;
  quote?: ReplyQuoteView | null;
  people?: ReadonlyMap<number, Profile>;
  viewerId?: number;
}) {
  const { t, locale } = useTranslation();
  const name = author.name.trim() || author.username;
  // One edit scope for the whole message: the head line's Edit control opens the editor in the bubble below.
  // `data-continues` is a turn's only statement about the block rhythm: `run` is a message that hangs under
  // the writer's previous one (the old `pt-1`), and the bare attribute is any other turn that flushes
  // against the one before it. A turn without it opens a block, whose gap chat.css owns.
  return <MessageEdit disabled={readOnly || !turn.settled}>{editor => (
    <div data-tk={tk} data-testid="chat-turn" data-role="you" data-message-id={turn.id} data-first={first ? '' : undefined} data-continues={!breakBefore ? 'run' : undefined} className="chat-turn chat-turn--you chat-turn--authored">
      <div className={`chat-author-run group/reactable${phone ? ' chat-author-run--phone' : ''}${mentionsYou ? ' chat-mention' : ''}`} data-mentions-you={mentionsYou ? '' : undefined} data-mention-unseen={mentionsYou && mentionUnseen ? '' : undefined}>
        {continuesRun ? <span className="sr-only">{name}</span> : (
          <AuthorHead name={name} avatar={<Avatar user={{ ...author, avatar: author.avatar ?? undefined }} size={!phone ? AUTHOR_AVATAR.regular : AUTHOR_AVATAR.small} />}>
            {turn.createdAt ? (
              <>
                <span aria-hidden className="text-small text-muted-foreground">·</span>
                <time dateTime={turn.createdAt} className="shrink-0 text-small text-muted-foreground">{localDateTime(turn.createdAt, locale, false)}</time>
              </>
            ) : null}
            {actions}
          </AuthorHead>
        )}
        {/* The reading anchor holds the bubble, not the block: an older page can make this message a
            continuation, and dropping its header must not move the text being read. */}
        <AIMessage align="end" data-chat-anchor="message">
          <MessageContent className="items-end">
            <UserBubble turn={turn} editor={editor} people={people} viewerId={viewerId} quote={quote} />
            {meta}
          </MessageContent>
        </AIMessage>
        {mentionsYou ? <span className="sr-only">{t.chat.mentionsYou}</span> : null}
      </div>
    </div>
  )}</MessageEdit>;
}

/** One turn that is not assistant work: the person's message, an event run or a compaction divider.
 *  Assistant steps are drawn by `AssistantRun`, one run at a time. Both surfaces use the same left-aligned
 *  role dot and label; a shared conversation draws human messages as author runs (`AuthoredMessage`).
 *
 *  Memoized, and that memo is load-bearing: a streaming answer replaces the transcript view on every token,
 *  so without it every settled turn in the conversation is reconciled tens of times a second — the work
 *  that leaves a keystroke waiting on a phone. */
export const Message = memo(function Message({ turn, models, showRole, breakBefore, first, tk, viewerId, reacting, people, continuesRun, phone, quoteFor, readOnly, mentionUnseen }: MessageProps) {
  const { t } = useTranslation();
  if (turn.role === 'divider') return <ContextDivider />;
  if (turn.role === 'event') return <SessionEvents events={turn.events} tk={tk} breakBefore={breakBefore} first={first} />;
  // The person's own controls go inline in the line that carries this message's timestamp, the same row the
  // agent's answer draws: the header line of a shared conversation's first message, otherwise the metadata
  // line below the bubble. Either way the row adds no side indent to the bubble.
  const actions = turn.id ? messageActionRow(turn, reacting, viewerId, turn.text, readOnly) : null;
  // The writer is named only in a shared conversation: the daemon records `author` there and nowhere else,
  // and only there can a message mention anyone.
  if (turn.author) {
    const mentionsYou = viewerId !== undefined && turn.mentions?.some((mention) => mention.id === viewerId) === true;
    // A message that continues the author's run has no header line: its time, and so its controls, live in the
    // metadata line below the bubble. The first message of the run states them in the header instead.
    const meta = continuesRun ? <MessageMeta turn={turn} models={models} actions={actions} /> : null;
    return <AuthoredMessage turn={turn} author={turn.author} readOnly={readOnly} breakBefore={breakBefore} first={first} tk={tk}
      continuesRun={continuesRun} phone={phone} quote={quoteFor?.(turn)} meta={meta}
      actions={continuesRun ? null : actions} mentionsYou={mentionsYou} mentionUnseen={mentionUnseen} people={people} viewerId={viewerId} />;
  }
  return <MessageEdit disabled={readOnly || !turn.settled}>{editor => (
      <div data-tk={tk} data-testid="chat-turn" data-role="you" data-message-id={turn.id} data-first={first ? '' : undefined} data-continues={!breakBefore && !continuesRun ? '' : undefined} className="chat-turn chat-turn--you grid grid-cols-[--spacing(4)_1fr] gap-x-3">
        <span className="chat-turn__mark-slot" aria-hidden>{showRole ? (
          <span className="chat-turn__marker mt-1.5 h-2 w-2 rounded-full bg-primary ring-4 ring-primary/15" />
        ) : <span className="chat-turn__marker" />}</span>
        <div className="chat-turn__column min-w-0">
          {showRole ? <div className="chat-turn__role mb-0.5 text-small-emphasis text-primary">{t.chat.roleYou}</div> : null}
          <AIMessage align="end" className="group/reactable">
            <MessageContent className="items-end">
              <UserBubble turn={turn} editor={editor} quote={quoteFor?.(turn)} />
              <MessageMeta turn={turn} models={models} actions={actions} />
            </MessageContent>
          </AIMessage>
        </div>
      </div>
  )}</MessageEdit>;
});
